import { jsPDF } from 'jspdf';
import api from './api';
import {
  getOrderPayableTotal,
  getOrderShippingCost,
  getOrderDiscount,
} from './orderAmount';
import { amountInWords } from './amountInWords';

export const KOT_ORDER_CAP = 50;

const PAGE_W = 80;
const MEASURE_H = 900;
const MARGIN = 4.5;
const FONT_SIZE = 8;
const FONT_SIZE_TITLE = 10;
const FONT_SIZE_BANNER = 9;
const LINE_GAP = 1.15;
const SECTION_GAP = 3.2;
const LOGO_W = 34;
const LOGO_RATIO = 179 / 282;
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/kot-logo.png`;

let cachedLogoDataUrl = null;
let logoLoadPromise = null;

function loadKotLogo() {
  if (cachedLogoDataUrl) return Promise.resolve(cachedLogoDataUrl);
  if (logoLoadPromise) return logoLoadPromise;

  logoLoadPromise = fetch(LOGO_SRC)
    .then((res) => {
      if (!res.ok) throw new Error('Logo fetch failed');
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            cachedLogoDataUrl = reader.result;
            resolve(cachedLogoDataUrl);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    )
    .catch(() => {
      logoLoadPromise = null;
      return null;
    });

  return logoLoadPromise;
}

const COMPANY = {
  name: 'COUNTRY CHICKEN CO',
  gstin: '36AAOCM5216B1ZN',
  fssai: '13624014000774',
};

function getStoreHeader(order) {
  const store = order.storeDetails || {};
  const addressParts = [store.address, store.pincode].filter(Boolean);
  return {
    name: COMPANY.name,
    address: addressParts.join(', '),
    gstin: store.gstin || COMPANY.gstin,
    mobile: store.contactNo || store.contactNumber || '',
    fssai: store.fssai || store.FSSAI || COMPANY.fssai,
  };
}

function formatRs(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatQty(qty) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return String(n);
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function toDate(value) {
  if (!value && value !== 0) return null;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') return new Date(value);
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  return null;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatClock(value) {
  const date = toDate(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function formatHms(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const [h = '00', m = '00', s = '00'] = timeStr.split(':');
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function getSlotRange(order) {
  const details = order.timeSlotDetails;
  if (!details) return { start: '', end: '' };
  return {
    start: formatHms(details.startTime),
    end: formatHms(details.endTime),
  };
}

function getCustomerMobile(order) {
  return (
    order.userId ||
    order.customer_mobile ||
    order.customerMobile ||
    order.customer_phone ||
    order.customerPhone ||
    order.phone ||
    order.mobile ||
    order.user?.mobile ||
    order.user?.phone ||
    order.deliveryAddress?.mobile ||
    order.deliveryAddress?.phone ||
    ''
  );
}

function formatAddress(addr) {
  if (!addr) return '';
  const parts = [
    addr.houseDetails,
    addr.streetDetails,
    addr.landmark ? `Near ${addr.landmark}` : null,
    addr.pincode,
  ].filter(Boolean);
  return parts.join(', ');
}

function getCustomizationEntries(product) {
  const customizations = product.customizations;
  if (!customizations || typeof customizations !== 'object') return [];

  const entries = [];
  Object.values(customizations).forEach((customization) => {
    if (Array.isArray(customization)) {
      customization.forEach((opt) => {
        const name = opt.optionName || opt.name;
        if (name) entries.push({ name, price: parseFloat(opt.additionalPrice) || 0 });
      });
      return;
    }

    if (customization?.uiType === 'checkbox' && Array.isArray(customization.selections)) {
      customization.selections.forEach((selection) => {
        const name = selection.optionName || selection.name;
        if (name) {
          entries.push({
            name,
            price: parseFloat(selection.additionalPrice) || 0,
          });
        }
      });
      return;
    }

    if (customization?.selection) {
      const sel = customization.selection;
      const name = sel.optionName || sel.name || (typeof sel === 'string' ? sel : null);
      if (name) {
        entries.push({
          name,
          price: parseFloat(sel.additionalPrice ?? customization.additionalPrice) || 0,
        });
      }
      return;
    }

    const optionName = customization?.optionName || customization?.name;
    if (optionName) {
      entries.push({
        name: optionName,
        price: parseFloat(customization.additionalPrice) || 0,
      });
    }
  });
  return entries;
}

function getUnitCustomizationTotal(product) {
  return getCustomizationEntries(product).reduce((sum, e) => sum + e.price, 0);
}

function getProductUnitPrice(product) {
  return parseFloat(product.variant?.price) || parseFloat(product.price) || 0;
}

function getProductLineTotal(product) {
  const qty = product.quantity ?? 1;
  return (getProductUnitPrice(product) + getUnitCustomizationTotal(product)) * qty;
}

function getLineQty(product) {
  const pieceQty = parseFloat(product.quantity);
  return Number.isFinite(pieceQty) && pieceQty > 0 ? pieceQty : 1;
}

function amountInWordsPlain(amount) {
  return amountInWords(amount)
    .replace(/ Rupees/gi, '')
    .replace(/ And .*Paise/gi, '')
    .replace(/ Only$/i, '')
    .trim() || 'Zero';
}

function getOrderBanner(order) {
  const delivery = String(order.deliveryType || order.delivery_type || '').toLowerCase();
  if (delivery.includes('pickup') || delivery.includes('self')) return 'PICKUP ORDER';
  return 'ONLINE ORDER';
}

/**
 * Draw one Focus-style 80mm slip. Returns the Y position after the last line.
 */
function drawSlip(doc, order, logoDataUrl = null) {
  const innerX = MARGIN;
  const maxX = PAGE_W - MARGIN;
  const contentW = maxX - innerX;
  const leftValueX = innerX + 22;
  const rightLabelX = innerX + 42;
  const rightValueX = maxX;
  const footColonX = maxX - 20;
  let cy = MARGIN;

  const lineHFor = (size) => size * 0.45;

  const setStyle = (bold, size = FONT_SIZE) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(0, 0, 0);
  };

  const writeLines = (text, { bold = false, align = 'left', size = FONT_SIZE } = {}) => {
    setStyle(bold, size);
    const lines = doc.splitTextToSize(String(text ?? ''), contentW);
    const lineH = lineHFor(size);
    lines.forEach((line) => {
      const x =
        align === 'center' ? PAGE_W / 2 : align === 'right' ? maxX : innerX;
      doc.text(line, x, cy + lineH * 0.85, { align });
      cy += lineH + LINE_GAP;
    });
  };

  const writeTwoColRow = (leftLabel, leftValue, rightLabel, rightValue) => {
    const y = cy + lineHFor(FONT_SIZE) * 0.85;
    setStyle(true);
    doc.text(leftLabel, innerX, y);
    setStyle(false);
    const leftMaxW = rightLabelX - leftValueX - 1.5;
    const leftLines = doc.splitTextToSize(String(leftValue ?? ''), Math.max(8, leftMaxW));
    doc.text(leftLines[0] || '', leftValueX, y);

    if (rightLabel) {
      setStyle(true);
      doc.text(rightLabel, rightLabelX, y);
      setStyle(false);
      doc.text(String(rightValue ?? ''), rightValueX, y, { align: 'right' });
    }

    cy += lineHFor(FONT_SIZE) + LINE_GAP;
    for (let i = 1; i < leftLines.length; i += 1) {
      setStyle(false);
      doc.text(leftLines[i], leftValueX, cy + lineHFor(FONT_SIZE) * 0.85);
      cy += lineHFor(FONT_SIZE) + LINE_GAP;
    }
  };

  const writeFootRow = (label, value, y) => {
    setStyle(true);
    doc.text(label, footColonX, y, { align: 'right' });
    setStyle(false);
    if (value) doc.text(value, rightValueX, y, { align: 'right' });
  };

  if (logoDataUrl) {
    const logoH = LOGO_W * LOGO_RATIO;
    doc.addImage(logoDataUrl, 'PNG', (PAGE_W - LOGO_W) / 2, cy, LOGO_W, logoH);
    cy += logoH + 1.8;
  }

  const header = getStoreHeader(order);

  writeLines(header.name, { bold: true, align: 'center', size: FONT_SIZE_TITLE });
  cy += 0.4;
  if (header.address) {
    writeLines(header.address, { align: 'center', size: 7 });
  }
  cy += 0.6;

  const gstY = cy + lineHFor(7) * 0.85;
  setStyle(true, 7);
  doc.text(`GSTIN : ${header.gstin || ''}`, innerX, gstY);
  doc.text(`Mobile: ${header.mobile || ''}`, maxX, gstY, { align: 'right' });
  cy += lineHFor(7) + LINE_GAP;

  const fssaiText = `FSSAI: ${header.fssai || ''}`;
  const fssaiY = cy + lineHFor(7) * 0.85;
  setStyle(true, 7);
  const fssaiLabelW = doc.getTextWidth('FSSAI: ');
  const fssaiFullW = doc.getTextWidth(fssaiText);
  const fssaiStart = (PAGE_W - fssaiFullW) / 2;
  doc.text('FSSAI: ', fssaiStart, fssaiY);
  setStyle(false, 7);
  doc.text(String(header.fssai || ''), fssaiStart + fssaiLabelW, fssaiY);
  cy += lineHFor(7) + LINE_GAP + 0.8;

  writeLines(getOrderBanner(order), {
    bold: true,
    align: 'center',
    size: FONT_SIZE_BANNER,
  });
  cy += SECTION_GAP;

  const orderId = order.orderId || order.id || '';
  const created = order.timeStamp || order.createdAt;
  const customerName =
    order.deliveryAddress?.userName || order.customer_name || order.customerName || '';

  writeTwoColRow('Invoice No :', orderId, 'Date :', formatDate(created));
  writeTwoColRow('Customer :', customerName, 'Time :', formatClock(created));
  writeTwoColRow('GSTIN :', '', 'Mobile :', getCustomerMobile(order));

  const addrLines = doc.splitTextToSize(
    formatAddress(order.deliveryAddress),
    maxX - leftValueX
  );
  const addrY = cy + lineHFor(FONT_SIZE) * 0.85;
  setStyle(true);
  doc.text('Address :', innerX, addrY);
  setStyle(false);
  if (addrLines.length) {
    doc.text(addrLines[0], leftValueX, addrY);
    cy += lineHFor(FONT_SIZE) + LINE_GAP;
    for (let i = 1; i < addrLines.length; i += 1) {
      doc.text(addrLines[i], leftValueX, cy + lineHFor(FONT_SIZE) * 0.85);
      cy += lineHFor(FONT_SIZE) + LINE_GAP;
    }
  } else {
    cy += lineHFor(FONT_SIZE) + LINE_GAP;
  }

  cy += SECTION_GAP;
  const qtyW = 13;
  const amtW = 17;
  const itemW = contentW - qtyW - amtW;
  const colItem = innerX;
  const colQty = innerX + itemW;
  const cellPad = 1.6;
  const rowLineH = lineHFor(FONT_SIZE) + 1.1;
  const tableTop = cy;

  cy += cellPad + 0.4;
  let y = cy + lineHFor(FONT_SIZE) * 0.85;
  setStyle(true);
  doc.text('Item/**', colItem + cellPad, y);
  doc.text('Qty', colQty + qtyW / 2, y, { align: 'center' });
  doc.text('Gross', maxX - cellPad, y, { align: 'right' });
  cy += rowLineH;
  const headerBottom = cy;

  const products = order.products || [];
  let qtySum = 0;
  let grossSum = 0;

  products.forEach((product, pIndex) => {
    if (pIndex > 0) cy += 0.8;
    const lineQty = getLineQty(product);
    const lineGross = getProductLineTotal(product);
    qtySum += lineQty;
    grossSum += lineGross;

    const leftLines = [
      ...doc.splitTextToSize(product.name || 'Item', itemW - cellPad * 2),
      ...getCustomizationEntries(product).map((c) => `** : ${c.name}`),
    ];

    leftLines.forEach((line, i) => {
      y = cy + lineHFor(FONT_SIZE) * 0.85;
      setStyle(false);
      doc.text(line, colItem + cellPad, y);
      if (i === 0) {
        doc.text(formatQty(lineQty), colQty + qtyW / 2, y, { align: 'center' });
        doc.text(formatRs(lineGross), maxX - cellPad, y, { align: 'right' });
      }
      cy += rowLineH;
    });
  });

  const totalTop = cy;
  cy += 0.8;
  y = cy + lineHFor(FONT_SIZE) * 0.85;
  setStyle(true);
  doc.text('Total', colItem + cellPad, y);
  doc.text(formatQty(qtySum), colQty + qtyW / 2, y, { align: 'center' });
  doc.text(formatRs(grossSum), maxX - cellPad, y, { align: 'right' });
  cy += rowLineH + cellPad;
  const tableBottom = cy;

  doc.setDrawColor(0);
  doc.setLineWidth(0.25);
  doc.rect(innerX, tableTop, contentW, tableBottom - tableTop);
  doc.line(innerX, headerBottom, maxX, headerBottom);
  doc.line(innerX, totalTop, maxX, totalTop);
  doc.line(colQty, tableTop, colQty, tableBottom);
  doc.line(colQty + qtyW, tableTop, colQty + qtyW, tableBottom);

  cy += SECTION_GAP;
  const discount = getOrderDiscount(order);
  const shipping = getOrderShippingCost(order);
  const total = getOrderPayableTotal(order);
  const paymentStatus = (order.paymentStatus || order.payment_status || '')
    .toString()
    .toUpperCase();
  const slot = getSlotRange(order);

  y = cy + lineHFor(FONT_SIZE) * 0.85;
  writeFootRow('Discount :', discount > 0 ? formatRs(discount) : '', y);
  y += lineHFor(FONT_SIZE) + LINE_GAP + 0.4;
  writeFootRow('Shipping Cost :', shipping > 0 ? formatRs(shipping) : '', y);
  y += lineHFor(FONT_SIZE) + LINE_GAP + 0.4;
  writeFootRow('Total Amt :', formatRs(total), y);
  cy = y + lineHFor(FONT_SIZE) + LINE_GAP + 1.2;

  setStyle(true);
  doc.text('Instructions:', innerX, cy + lineHFor(FONT_SIZE) * 0.85);
  cy += lineHFor(FONT_SIZE) + LINE_GAP;
  const instrText = String(order.instructions || '').trim();
  if (instrText) {
    writeLines(instrText);
  }
  cy += 1.2;

  y = cy + lineHFor(FONT_SIZE) * 0.85;
  setStyle(true);
  doc.text('Payment Status :', innerX, y);
  setStyle(false);
  doc.text(paymentStatus, leftValueX + 8, y);
  cy += lineHFor(FONT_SIZE) + LINE_GAP + SECTION_GAP;

  writeLines('Amount in words :', { bold: true });
  cy += 0.3;
  writeLines(amountInWordsPlain(total));
  cy += SECTION_GAP;

  y = cy + lineHFor(FONT_SIZE) * 0.85;
  setStyle(true);
  doc.text('Slot Timings :', innerX, y);
  setStyle(false);
  let sx = leftValueX + 4;
  doc.text(slot.start || '', sx, y);
  sx += Math.max(doc.getTextWidth(slot.start || ''), 14) + 3;
  setStyle(true);
  doc.text('To', sx, y);
  sx += doc.getTextWidth('To') + 3;
  setStyle(false);
  doc.text(slot.end || '', sx, y);
  cy += lineHFor(FONT_SIZE) + LINE_GAP + SECTION_GAP;

  writeLines('THANK YOU', { bold: true, align: 'center' });
  cy += 0.4;
  writeLines('VISIT AGAIN', { bold: true, align: 'center' });

  return cy + MARGIN + 4;
}

function measureSlipHeight(order, logoDataUrl = null) {
  const doc = new jsPDF({ unit: 'mm', format: [PAGE_W, MEASURE_H], orientation: 'portrait' });
  const endY = drawSlip(doc, order, logoDataUrl);
  return Math.min(MEASURE_H, Math.max(120, Math.ceil(endY + 4)));
}

function timestampName() {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

/**
 * Build one 80mm PDF (one page per order) and open it in a new tab.
 * Falls back to download if the popup is blocked.
 * @param {object[]} orders
 * @param {Window | null} [existingTab] tab opened during the user click, so blockers allow it
 * @returns {Promise<{ orderCount: number, opened: boolean }>}
 */
export async function openKotPdfs(orders, existingTab = null) {
  if (!orders?.length) {
    throw new Error('No orders selected');
  }
  if (orders.length > KOT_ORDER_CAP) {
    throw new Error(`Cannot print more than ${KOT_ORDER_CAP} orders at a time`);
  }

  const logoDataUrl = await loadKotLogo();
  const heights = orders.map((order) => measureSlipHeight(order, logoDataUrl));
  const doc = new jsPDF({
    unit: 'mm',
    format: [PAGE_W, heights[0]],
    orientation: 'portrait',
  });
  drawSlip(doc, orders[0], logoDataUrl);

  for (let i = 1; i < orders.length; i += 1) {
    doc.addPage([PAGE_W, heights[i]], 'portrait');
    drawSlip(doc, orders[i], logoDataUrl);
  }

  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);

  if (existingTab && !existingTab.closed) {
    existingTab.location.href = url;
    return { orderCount: orders.length, opened: true };
  }

  const opened = window.open(url, '_blank');
  if (!opened) {
    doc.save(`KOT-${timestampName()}.pdf`);
  }

  return { orderCount: orders.length, opened: Boolean(opened) };
}

/**
 * Load full orders by id from the server, then open the KOT PDF.
 * @param {string[]} orderIds
 */
export async function fetchAndOpenKotPdfs(orderIds) {
  if (!orderIds?.length) {
    throw new Error('No orders selected');
  }
  if (orderIds.length > KOT_ORDER_CAP) {
    throw new Error(`Cannot print more than ${KOT_ORDER_CAP} orders at a time`);
  }

  const previewTab = window.open('about:blank', '_blank');

  try {
    const response = await api.post('/orders/by-ids', { orderIds });
    if (!response.data?.success || !Array.isArray(response.data.orders)) {
      throw new Error(response.data?.message || 'Failed to load orders for KOT');
    }
    return await openKotPdfs(response.data.orders, previewTab);
  } catch (err) {
    if (previewTab && !previewTab.closed) {
      previewTab.close();
    }
    const apiMessage = err.response?.data?.message;
    throw new Error(apiMessage || err.message || 'Failed to load orders for KOT');
  }
}
